import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { MapPin, Car } from "@phosphor-icons/react";
import { pick } from "@/lib/languages";
import { completeAddress, placeDetails } from "@/lib/places";
import usePlaceSearch from "@/hooks/usePlaceSearch";
import { PLACES_L } from "@/i18n/places";
import { nameMatches } from "@/components/LocationAutocomplete";

/**
 * An address field that suggests places as the person types, from Google
 * Places behind Toukky's API (lib/places.js): the browser never calls Google
 * and the key never reaches it. Nothing is asked until the person has typed
 * two characters into the field; a value that arrives from a saved listing or
 * a link is never looked up. When the API answers that no provider is
 * configured, or a cap is reached, the field is plain free text.
 *
 * Props (all optional except value/onChange; the defaults are the original
 * behaviour):
 *   value, onChange(text)  the controlled text
 *   onSelect(suggestion)   the raw suggestion of a pick
 *   onPlace(place | null)  { place_id, label } on a pick; with `resolve`, once
 *                          more with the address parts { locality,
 *                          sublocality, postal_code, country_code, country,
 *                          formatted_address } when they arrive (never
 *                          coordinates); null on every edit, so a stale
 *                          place_id is never sent with typed-over text.
 *                          Always called after onChange: use functional
 *                          state updates, and return the same state when
 *                          nothing changes.
 *   resolve                look the pick's address parts up (/places/details)
 *   exact                  a pick ends as Google's complete address: the
 *                          label first, then, once /places/details answers,
 *                          the place's name and formatted_address (number,
 *                          street, postal code, town, country) through
 *                          onChange, only while the field still holds the
 *                          label. Not a lookup, not an edit: onPlace keeps
 *                          the pick. With `resolve` too, one details call
 *                          serves both. A failure keeps the label, and so
 *                          does a complete address longer than maxLength.
 *   kind                   "address" | "locality" (cities and areas only)
 *   scope                  "near_sn" (default: anywhere in the world,
 *                          Senegal first) | "sn" (Senegal only)
 *   pickValue(suggestion)  what a pick writes in the field (default label)
 *   towns                  town names offered before the addresses (the car
 *                          pick-up: where Toukky has cars); a pick writes the
 *                          town as typed, never looked up. All of them on an
 *                          empty field, the ones the text starts otherwise.
 *   lang, placeholder, testId, className, required, id, naked, ariaLabel,
 *   maxLength, aria-describedby
 */

// Room kept between the list and whatever covers the page.
const VIEW_GAP = 8;
// The shortest the list gets when the page cannot scroll far enough: about
// two suggestions, still scrollable.
const MIN_LIST_PX = 96;

/**
 * The part of the window a person can see, in viewport pixels: below a
 * header stuck to the top, above anything marked data-covers-bottom (the
 * cookie banner) and above the on-screen keyboard (the visual viewport).
 */
export function visibleBand() {
  const viewport = window.visualViewport;
  let top = viewport ? viewport.offsetTop : 0;
  let bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
  for (const header of document.querySelectorAll("header")) {
    const { position } = window.getComputedStyle(header);
    const box = header.getBoundingClientRect();
    if ((position === "sticky" || position === "fixed") && box.top <= top + 1 && box.bottom > top) top = box.bottom;
  }
  for (const cover of document.querySelectorAll("[data-covers-bottom]")) {
    const box = cover.getBoundingClientRect();
    if (box.height > 0 && box.top < bottom) bottom = box.top;
  }
  return { top, bottom };
}

export default function AddressAutocomplete({
  value, onChange, onSelect, onPlace, resolve = false, exact = false, kind = "address", scope = "near_sn", pickValue,
  lang = "fr", placeholder, testId, className = "", required = false, id, naked = false, ariaLabel, maxLength,
  "aria-describedby": describedBy, towns = [],
}) {
  const t = pick(PLACES_L, lang);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  // Only text the person typed is looked up, and only while it is still the
  // field's value: never a saved value, never a pick, never text the form
  // writes itself (a city filled from a picked address, a taxi route row
  // moving up after a deletion). Each of those would be a billed lookup.
  const [typed, setTyped] = useState(null);
  const { suggestions, loading, outcome, renewSession, clear } = usePlaceSearch({
    query: value, lang, kind, scope, enabled: typed !== null && typed === value,
  });
  const containerRef = useRef(null);
  const fieldRef = useRef(null);
  const popupRef = useRef(null);
  const listRef = useRef(null);
  const [listMax, setListMax] = useState(null);
  // A details answer belongs to the pick that asked for it; any later pick,
  // edit or unmount makes it stale.
  const pickRef = useRef(0);
  // An exact field's complete address replaces the label only while the
  // field still holds it (the form may have cleared or rewritten it since).
  const valueRef = useRef(value);
  const [completing, setCompleting] = useState(false);
  const generatedId = useId();
  const inputId = id || generatedId;
  const listId = `${inputId}-suggestions`;
  const optionId = (i) => `${inputId}-opt-${i}`;
  // The towns apply to text the person typed (or an empty field), never to a
  // value the form arrived with.
  const townsShown = !towns.length ? [] : !value ? towns : typed !== null && typed === value ? towns.filter((name) => nameMatches(name, value)) : [];
  const options = [...townsShown.map((name) => ({ town: name, place_id: `town:${name}` })), ...suggestions];
  const hasList = open && options.length > 0;
  const popupVisible = open && (options.length > 0 || loading);
  const announcement = !open ? "" : outcome === "results" ? t.results(options.length) : outcome === "empty" && !townsShown.length ? t.none : "";

  useEffect(() => {
    const tickets = pickRef;
    return () => { tickets.current += 1; };
  }, []);

  useLayoutEffect(() => { valueRef.current = value; }, [value]);

  useEffect(() => { setActive(-1); }, [suggestions]);

  // On a phone the list opened under the cookie banner or the keyboard. When
  // it would be hidden, the page scrolls the field up (never under the
  // header) until the list shows; where the page cannot scroll that far, the
  // list gets shorter and scrolls inside. Measured before paint, so nothing
  // jumps.
  useLayoutEffect(() => {
    if (!popupVisible) { setListMax(null); return; }
    const field = fieldRef.current;
    const popup = popupRef.current;
    if (!field || !popup) return;
    const band = visibleBand();
    const hidden = popup.getBoundingClientRect().bottom + VIEW_GAP - band.bottom;
    if (hidden <= 0) return;
    const room = field.getBoundingClientRect().top - VIEW_GAP - band.top;
    if (room > 0) window.scrollBy(0, Math.min(hidden, room));
    const list = listRef.current;
    if (!list) return;
    const after = visibleBand();
    const stillHidden = popup.getBoundingClientRect().bottom + VIEW_GAP - after.bottom;
    if (stillHidden > 0) setListMax(Math.max(MIN_LIST_PX, Math.floor(list.getBoundingClientRect().height - stillHidden)));
  }, [popupVisible, options.length]);

  useEffect(() => {
    if (active < 0) return;
    listRef.current?.children?.[active]?.scrollIntoView?.({ block: "nearest" });
  }, [active]);

  // A tap or click anywhere else closes the list (pointerdown also covers touch).
  useEffect(() => {
    const onOutside = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onOutside);
    return () => document.removeEventListener("pointerdown", onOutside);
  }, []);

  const close = () => { setOpen(false); setActive(-1); };

  const choose = (s) => {
    if (s.town) {
      pickRef.current += 1;
      setTyped(null);
      setCompleting(false);
      clear();
      close();
      onChange(s.town);
      onPlace?.(null);
      return;
    }
    const text = (pickValue ? pickValue(s) : s.label) || s.label;
    const session = renewSession();
    const ticket = ++pickRef.current;
    setTyped(null);
    clear();
    close();
    onChange(text);
    onSelect?.(s);
    const base = { place_id: s.place_id, label: s.label };
    onPlace?.(base);
    const parts = resolve && Boolean(onPlace);
    if (!exact && !parts) return;
    if (exact) setCompleting(true);
    // One details call, under the keystrokes' session, for the complete
    // address and the parts alike.
    placeDetails({ placeId: s.place_id, session, lang }).then((details) => {
      if (ticket !== pickRef.current) return;
      setCompleting(false);
      if (!details) return;
      const full = exact ? completeAddress(s.main, details.formatted_address) : "";
      // Written as the form's own text: never looked up, never an edit. One
      // the field could not hold (the API's limit) leaves the label.
      const fits = !(maxLength > 0) || full.length <= maxLength;
      if (full && fits && full !== text && valueRef.current === text) onChange(full);
      if (parts) onPlace({ ...base, ...details, place_id: s.place_id });
    });
  };

  const onInput = (e) => {
    setTyped(e.target.value);
    setOpen(true);
    onChange(e.target.value);
    // Typed text is no longer the picked place (nor a saved one): every edit
    // drops a details answer still on its way, and says so.
    pickRef.current += 1;
    setCompleting(false);
    onPlace?.(null);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { close(); return; }
    if (!hasList) { if (e.key === "ArrowDown") setOpen(true); return; }
    const last = options.length - 1;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i >= last ? 0 : i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i <= 0 ? last : i - 1)); }
    // Home and End move through the list once it is being browsed; before
    // that they move the caret in the text, as in any field.
    else if ((e.key === "Home" || e.key === "End") && active >= 0) { e.preventDefault(); setActive(e.key === "Home" ? 0 : last); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(options[active]); }
  };

  // Tabbing away (or any focus leaving the field and its list) closes it.
  const onBlur = (e) => { if (!containerRef.current?.contains(e.relatedTarget)) close(); };

  return (
    <div ref={containerRef} className="relative w-full" onBlur={onBlur}>
      <div ref={fieldRef} className="relative">
        {!naked && <MapPin size={16} weight="bold" aria-hidden="true" className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-touki-muted" />}
        <input
          id={inputId}
          data-testid={testId}
          value={value}
          required={required}
          maxLength={maxLength}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-describedby={describedBy}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          role="combobox"
          aria-expanded={hasList}
          aria-controls={hasList ? listId : undefined}
          aria-autocomplete="list"
          aria-busy={loading || completing || undefined}
          aria-activedescendant={hasList && active >= 0 && active < options.length ? optionId(active) : undefined}
          onChange={onInput}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className={naked
            ? `w-full border-0 h-7 focus:outline-none text-body-md font-semibold text-touki-black placeholder:text-touki-muted placeholder:font-normal bg-transparent ${className}`
            : `flex h-11 w-full rounded-md border border-touki bg-white ps-9 pe-3 text-base sm:text-sm text-touki-black placeholder:text-touki-muted focus-visible:outline-none focus-visible:border-touki-black focus-visible:ring-1 focus-visible:ring-touki-black ${className}`}
        />
      </div>
      {/* Read out once the answer is in, not on every keystroke. */}
      <span role="status" aria-live="polite" className="sr-only" data-testid={testId ? `${testId}-status` : undefined}>{announcement}</span>
      {popupVisible && (
        // Pressing anywhere in the popup (an option, its scrollbar, the
        // attribution) keeps the focus in the field, so blur does not close it.
        <div ref={popupRef} data-testid={testId ? `${testId}-popup` : undefined} onMouseDown={(e) => e.preventDefault()}
          className="absolute z-50 top-full inset-x-0 mt-1 bg-white border border-touki rounded-lg shadow-lg overflow-hidden">
          {options.length > 0 && (
            <div ref={listRef} id={listId} role="listbox" aria-label={t.list} data-testid={`${testId}-suggestions`}
              style={listMax ? { maxHeight: listMax } : undefined}
              className="max-h-[min(20rem,50dvh)] overflow-auto overscroll-contain">
              {options.map((s, i) => (
                <button key={s.place_id} id={optionId(i)} role="option" aria-selected={i === active} type="button" tabIndex={-1}
                  onMouseEnter={() => setActive(i)} onClick={() => choose(s)}
                  data-testid={s.town ? "address-town-option" : `address-suggestion-${s.place_id}`}
                  className={`w-full min-h-11 text-start px-4 py-2.5 border-b border-touki last:border-b-0 flex items-center gap-2 ${i === active ? "bg-touki-light" : "hover:bg-touki-light"}`}>
                  <span className="h-8 w-8 rounded-md flex items-center justify-center shrink-0 bg-touki-light text-touki-black" aria-hidden="true">{s.town ? <Car size={14} weight="bold" /> : <MapPin size={14} weight="bold" />}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-touki-black truncate">{s.town || s.main || s.label}</span>
                    {s.town ? <span className="block text-eyebrow text-touki-muted truncate">{t.carTown}</span>
                      : s.secondary && <span className="block text-eyebrow text-touki-muted truncate">{s.secondary}</span>}
                  </span>
                </button>
              ))}
            </div>
          )}
          {loading && suggestions.length === 0 && <div className="px-4 py-3 text-xs text-touki-muted">{t.searching}</div>}
          {/* Google's text attribution, outside the listbox (only options
              belong in one), shown whenever its suggestions are. */}
          {suggestions.length > 0 && (
            <div className="border-t border-touki px-4 py-1.5 text-end" data-testid={testId ? `${testId}-attribution` : undefined}>
              <span className="tk-google-attribution" translate="no">Google Maps</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
// The partner wizards' <Field> labels a single control with `for`; this
// field counts as one (the name survives minification).
AddressAutocomplete.displayName = "AddressAutocomplete";
